import React, { useState } from "react";
import { Star, X, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import api from "../lib/api";

function ReviewButtons({ reviews, claims, onClaimed }) {
  const [busy, setBusy] = useState(null);
  const platforms = [
    { key: "google", label: "Review on Google", color: "bg-white border border-slate-200 text-ink hover:bg-stone" },
    { key: "yelp", label: "Review on Yelp", color: "bg-[#d32323] text-white hover:opacity-90" },
  ];

  const claim = async (key, url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setBusy(key);
    try {
      const { data } = await api.post("/member/reviews/claim", { platform: key });
      if (!data.already) toast.success(data.message);
      await onClaimed?.();
    } catch {
      toast.error("Could not record your reward — please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {platforms.map(({ key, label, color }) => {
        const cfg = reviews?.[key];
        const claimed = claims?.[key];
        return (
          <button
            key={key}
            disabled={claimed || busy === key || !cfg}
            onClick={() => claim(key, cfg?.url)}
            data-testid={`review-${key}-btn`}
            className={`relative rounded-xl h-14 px-5 flex items-center justify-between font-medium transition-all disabled:opacity-70 ${claimed ? "bg-green-50 border border-green-200 text-green-700" : color}`}
          >
            <span className="flex items-center gap-2">
              {claimed ? <Check className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
              {claimed ? `${key === "google" ? "Google" : "Yelp"} — claimed` : label}
            </span>
            {!claimed && cfg && (
              <span className={`text-sm font-semibold ${key === "yelp" ? "text-white" : "text-iri-mid"}`}>+{cfg.points} pts</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

const Heading = () => (
  <>
    <div className="flex gap-1 justify-center mb-3">
      {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-iri-mid text-iri-mid" />)}
    </div>
    <h2 className="font-heading text-2xl font-light text-ink text-center">Love your results? Get rewarded.</h2>
    <p className="text-sm text-[#5C6664] text-center mt-2">
      Leave us a 5-star review and earn reward points instantly — <strong>50 points</strong> for Google, <strong>150 points</strong> for Yelp.
    </p>
  </>
);

export function ReviewCard({ reviews, claims, onClaimed, onDismiss }) {
  return (
    <div className="relative bg-white rounded-2xl border border-slate-100 p-7 mb-6" data-testid="review-reward-card">
      <button onClick={onDismiss} aria-label="Dismiss" data-testid="review-card-dismiss" className="absolute top-4 right-4 text-[#8A9492] hover:text-ink transition-colors">
        <X className="h-4 w-4" />
      </button>
      <Heading />
      <div className="mt-5 max-w-lg mx-auto">
        <ReviewButtons reviews={reviews} claims={claims} onClaimed={onClaimed} />
      </div>
    </div>
  );
}

export function ReviewModal({ open, onOpenChange, reviews, claims, onClaimed }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-[1.5rem]" data-testid="review-reward-modal">
        <div className="pt-2">
          <Heading />
          <div className="mt-6">
            <ReviewButtons reviews={reviews} claims={claims} onClaimed={onClaimed} />
          </div>
          <button onClick={() => onOpenChange(false)} data-testid="review-modal-later" className="mt-5 w-full text-sm text-[#8A9492] hover:text-ink transition-colors">
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
